<template>
  <div class="relative inline-block w-full">
    <!-- Dropdown Button -->
    <button
      @click="toggleDropdown"
      @blur="handleBlur"
      class="group relative flex items-center justify-between p-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-300 ease-out min-w-[160px] sm:min-w-[180px]"
      :class="{ 
        'border-blue-400 shadow-sm ring-2 ring-blue-500/20': isOpen,
        'hover:bg-gray-50': !isOpen 
      }"
    >
      <span class="truncate text-left">
        {{ selectedOption || placeholder }}
      </span>
      
      <!-- Chevron Icon -->
      <div class="ml-3 flex items-center">
        <svg
          class="h-3 w-3 text-gray-400 transition-all duration-300 ease-out group-hover:text-gray-600"
          :class="{ 
            'rotate-180 text-blue-500': isOpen,
            'text-gray-400': !isOpen 
          }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </button>

    <!-- Dropdown Menu -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-class="opacity-0 scale-95 translate-y-1"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-1"
    >
      <div
        v-if="isOpen"
        class="absolute z-50 mt-2 w-auto origin-top-right bg-white border border-gray-100 rounded-xl shadow-lg ring-1 ring-black/5 backdrop-blur-sm"
      >
        <div class="p-1">
          <!-- Clear Filter Option -->
          <button
            @click="selectOption(null)"
            class="group flex items-center w-full text-sm text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700 transition-all duration-200 ease-out"
            :class="{ 
              'bg-blue-50 text-blue-600 font-medium': selectedOption === null 
            }"
          >
          <div class="flex justify-center text-center items-center p-2">
              <span>{{ placeholder }}</span>
          </div>
          </button>
          
          <!-- Divider -->
          <div class="h-px bg-gray-100 my-1 mx-2"></div>
          
          <!-- Filter Options -->
          <button
            v-for="(option, index) in options"
            :key="option.value"
            @click="selectOption(option)"
            class="group flex items-center w-full px-1.5 py-0.5 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 ease-out"
            :class="{ 
              'bg-blue-50 text-blue-600 font-medium': selectedOption === option.value 
            }"
            :style="{ 'animation-delay': `${index * 30}ms` }"
          >
            <div class="flex flex-row p-2 justify-between">
                <span class="text-left">{{ option.label }}</span>
                
                <!-- Count Badge -->
                <span 
                  v-if="option.count" 
                  class="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full transition-all duration-200"
                  :class="{ 
                    'bg-blue-100 text-blue-600': selectedOption === option.value 
                  }"
                >
                  {{ option.count }}
                </span>
            </div>
            <!-- Label -->
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'FilterDropdown',
  props: {
    options: {
      type: Array,
      default() {
        return [
          { value: 'active', label: 'Active', color: 'bg-emerald-400', count: 12 },
          { value: 'pending', label: 'Pending', color: 'bg-amber-400', count: 5 },
          { value: 'inactive', label: 'Inactive', color: 'bg-red-400', count: 3 },
          { value: 'draft', label: 'Draft', color: 'bg-slate-400', count: 8 }
        ];
      }
    },
    selected: {
        type: [String, null],
        default: null
    },
    placeholder: {
        type: String,
        default: 'Select Item'
    }
  },
  data() {
    return {
      isOpen: false,
      selectedOption: this.getLabelFromValue(this.selected)
    };
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    selectOption(option) {
      if (option === null) {
        this.selectedOption = null;
        this.$emit('filter-change', null);
      } else {
        this.selectedOption = option.label;
        this.$emit('filter-change', option.value);
      }
      this.isOpen = false;
    },
    handleBlur(event) {
      setTimeout(() => {
        if (!event.relatedTarget || !event.currentTarget?.contains(event.relatedTarget)) {
          this.isOpen = false;
        }
      }, 150);
    },
    handleClickOutside(event) {
      const dropdown = event.target.closest('.relative');
      if (!dropdown) {
        this.isOpen = false;
      }
    },
    getLabelFromValue(value) {
      const match = this.options.find(opt => opt.value === value);
      return match ? match.label : null;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>